import { Container, Matrix, Sprite, Texture } from "pixi.js"

/**
 * Where a soft shadow's copies land, round its own place: the centre, and a ring `softness` px out at
 * eight bearings. One copy (sharp) at no softness.
 */
export const softShadowOffsets = (softness: number): { x: number; y: number }[] => {
    if (!(softness > 0)) return [{ x: 0, y: 0 }]
    const offsets = [{ x: 0, y: 0 }]
    for (let i = 0; i < 8; i++) {
        const angle = i * Math.PI / 4
        offsets.push({ x: Math.cos(angle) * softness, y: Math.sin(angle) * softness })
    }
    return offsets
}

/** Each of `copies` overlapping copies' alpha, so that where all of them overlap they show `alpha`. */
export const copyAlpha = (alpha: number, copies: number): number =>
    alpha >= 1 ? 1 : 1 - Math.pow(1 - Math.max(0, alpha), 1 / copies)

/**
 * A body's shadow, softened like the world's (building_shadow.ts blurs those once, as it bakes them)
 * without a live blur: drawn once per softShadowOffsets copy, each at the share of the alpha that stacks
 * back to the whole where they all overlap, so the edge falls off over 2 x `softness` px. Every copy is
 * the same texture and tint, batched with the body: no filter, no extra pass, no bake.
 */
export class SoftShadow extends Container {
    private copies: Sprite[] = []
    private readonly place = new Matrix()

    /** `texture` drawn through `matrix` at `alpha`, tinted `tint`, its edge softened over `softness` px. */
    show(texture: Texture, matrix: Matrix, alpha: number, tint: number, softness: number): void {
        const offsets = softShadowOffsets(softness)
        while (this.copies.length < offsets.length) {
            const copy = new Sprite(Texture.EMPTY)
            this.copies.push(copy)
            this.addChild(copy)
        }
        const each = copyAlpha(alpha, offsets.length)
        this.copies.forEach((copy, i) => {
            const offset = offsets[i]
            copy.visible = offset !== undefined
            if (!offset) return
            copy.texture = texture
            if (copy.tint !== tint) copy.tint = tint
            copy.alpha = each
            this.place.copyFrom(matrix)
            this.place.tx += offset.x
            this.place.ty += offset.y
            copy.transform.setFromMatrix(this.place)
        })
        this.visible = true
    }

    /** Hidden, and let go of the texture it drew (the generator reclaims the body's frames). */
    release(): void {
        this.visible = false
        this.copies.forEach(copy => { copy.texture = Texture.EMPTY })
    }
}
